import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Download, Trash2, MessageCircle, Mail, RefreshCw, Check,
  Users, CreditCard, CheckCircle2, Clock, Wallet, ShieldCheck,
} from "lucide-react";
import {
  adminLogin, adminMe, adminStats, adminConsultations,
  adminPayments, adminDeleteConsultation, adminDeletePayment, adminExportUrl,
  confirmPayment,
} from "@/lib/api";
import { waLink } from "@/lib/wa";
import { trackEvent } from "@/lib/analytics";

const formatRp = (n) => "Rp " + Number(n).toLocaleString("id-ID");
const formatDate = (iso) => new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" });

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("aiat_admin_token"));

  useEffect(() => {
    if (!authed) return;
    adminMe().catch(() => {
      localStorage.removeItem("aiat_admin_token");
      setAuthed(false);
    });
  }, [authed]);

  if (!authed) return <LoginScreen onSuccess={() => setAuthed(true)} />;
  return <Dashboard onLogout={() => { localStorage.removeItem("aiat_admin_token"); setAuthed(false); }} />;
}

function LoginScreen({ onSuccess }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await adminLogin({ email, password });
      localStorage.setItem("aiat_admin_token", res.token);
      toast.success("Login berhasil");
      trackEvent("admin_login_success");
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login gagal");
      trackEvent("admin_login_error", { status: err?.response?.status || 0 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-gradient-to-br from-sky-50 to-cyan-50">
      <div className="w-full max-w-md">
        <button onClick={() => navigate("/")} className="mb-6 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-sky-600" data-testid="admin-back-home">
          ← Kembali ke beranda
        </button>
        <form onSubmit={submit} className="bg-white border border-sky-100 rounded-3xl p-8 shadow-2xl shadow-sky-100" data-testid="admin-login-form">
          <div className="flex items-center gap-3 mb-8">
            <span className="w-12 h-12 rounded-full bg-gradient-to-br from-sky-400 to-cyan-500 text-white flex items-center justify-center font-display shadow-md">AT</span>
            <div>
              <div className="font-display text-2xl text-slate-900">Admin Panel</div>
              <div className="text-xs text-slate-500">AI AT Studio Design & Build</div>
            </div>
          </div>
          <label className="block mb-4">
            <span className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2 block">Email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-[#F8FBFE] border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-sky-500 focus:bg-white focus:ring-2 focus:ring-sky-100"
              data-testid="admin-email"
              placeholder="admin@aiatstudio.id"
            />
          </label>
          <label className="block mb-6">
            <span className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2 block">Password</span>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-[#F8FBFE] border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-sky-500 focus:bg-white focus:ring-2 focus:ring-sky-100"
              data-testid="admin-password"
            />
          </label>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600 disabled:opacity-50 text-white font-semibold text-sm uppercase tracking-[0.2em] py-4 rounded-full shadow-lg shadow-sky-200"
          >
            {loading ? "Memproses…" : "Masuk"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("payments");
  const [stats, setStats] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, c, p] = await Promise.all([adminStats(), adminConsultations(), adminPayments()]);
      setStats(s);
      setConsultations(c);
      setPayments(p);
    } catch {
      toast.error("Gagal memuat data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const exportXlsx = async () => {
    try {
      const token = localStorage.getItem("aiat_admin_token");
      const res = await fetch(adminExportUrl(), { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `aiat-studio-${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("File Excel terdownload");
    } catch {
      toast.error("Gagal export");
    }
  };

  const removeCons = async (id) => {
    if (!window.confirm("Hapus konsultasi ini?")) return;
    await adminDeleteConsultation(id);
    setConsultations((c) => c.filter((x) => x.id !== id));
    toast.success("Dihapus");
  };
  const removePay = async (id) => {
    if (!window.confirm("Hapus pembayaran ini?")) return;
    await adminDeletePayment(id);
    setPayments((p) => p.filter((x) => x.id !== id));
    toast.success("Dihapus");
  };

  const confirmPay = async (id) => {
    if (!window.confirm("Sudah verifikasi struk dari customer? Tindakan ini akan mengaktifkan unduhan desain.")) return;
    try {
      await confirmPayment(id);
      toast.success("Pembayaran dikonfirmasi — customer dapat mengunduh desain");
      load();
    } catch {
      toast.error("Gagal konfirmasi");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/40">
      <header className="bg-white border-b border-sky-100 sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-400 to-cyan-500 text-white flex items-center justify-center font-display shadow-md">AT</span>
            <div>
              <div className="font-display text-lg text-slate-900">Admin Dashboard</div>
              <div className="text-xs text-slate-500">AI AT Studio</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/")} className="hidden md:inline-flex text-xs uppercase tracking-[0.2em] text-slate-600 hover:text-sky-600 px-4 py-2">
              Beranda
            </button>
            <button onClick={load} className="p-2 hover:bg-sky-50 rounded-lg text-slate-600" data-testid="admin-refresh">
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              onClick={exportXlsx}
              data-testid="admin-export-button"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-sky-500 to-cyan-500 text-white text-xs uppercase tracking-[0.2em] px-4 py-2 rounded-full shadow-md shadow-sky-200 hover:shadow-lg"
            >
              <Download size={14} /> Export Excel
            </button>
            <button onClick={onLogout} className="p-2 hover:bg-red-50 rounded-lg text-red-500" data-testid="admin-logout">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-12 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
          <StatCard icon={Users} label="Konsultasi" value={stats?.consultations ?? "-"} color="from-sky-500 to-cyan-500" />
          <StatCard icon={CreditCard} label="Total Order" value={stats?.payments ?? "-"} color="from-indigo-500 to-blue-500" />
          <StatCard icon={ShieldCheck} label="Menunggu" value={stats?.awaiting_verification ?? "-"} color="from-amber-500 to-orange-500" />
          <StatCard icon={CheckCircle2} label="Lunas" value={stats?.paid ?? "-"} color="from-emerald-500 to-green-500" />
          <StatCard icon={Clock} label="Belum Bayar" value={stats?.pending ?? "-"} color="from-slate-400 to-slate-500" />
          <StatCard icon={Wallet} label="Revenue" value={stats ? formatRp(stats.revenue) : "-"} color="from-slate-700 to-slate-900" />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-200">
          <TabButton active={tab === "payments"} onClick={() => setTab("payments")} testid="tab-payments">
            Pembayaran ({payments.length})
          </TabButton>
          <TabButton active={tab === "consultations"} onClick={() => setTab("consultations")} testid="tab-consultations">
            Konsultasi ({consultations.length})
          </TabButton>
        </div>

        {tab === "payments" ? (
          <PaymentTable rows={payments} onDelete={removePay} onConfirm={confirmPay} />
        ) : (
          <ConsultationTable rows={consultations} onDelete={removeCons} />
        )}
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, wide }) {
  return (
    <div className={`bg-white border border-sky-100 rounded-2xl p-5 shadow-sm ${wide ? "lg:col-span-1" : ""}`}>
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center mb-3`}>
        <Icon size={18} className="text-white" />
      </div>
      <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</div>
      <div className="font-display text-2xl text-slate-900 mt-1 truncate">{value}</div>
    </div>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-4 py-3 text-sm uppercase tracking-[0.15em] font-semibold border-b-2 -mb-px transition-colors ${
        active ? "border-sky-500 text-sky-600" : "border-transparent text-slate-500 hover:text-slate-800"
      }`}
    >
      {children}
    </button>
  );
}

function PaymentTable({ rows, onDelete, onConfirm }) {
  if (!rows.length) return <Empty msg="Belum ada pembayaran." />;
  const badge = (status) => {
    if (status === "paid") return { cls: "bg-emerald-100 text-emerald-700", label: "Lunas" };
    if (status === "awaiting_verification") return { cls: "bg-amber-100 text-amber-700", label: "Menunggu Verifikasi" };
    return { cls: "bg-slate-100 text-slate-600", label: "Pending" };
  };
  return (
    <div className="bg-white border border-sky-100 rounded-2xl overflow-hidden shadow-sm" data-testid="payments-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-sky-50/60 text-[11px] uppercase tracking-[0.15em] text-slate-600">
            <tr>
              <Th>Tanggal</Th><Th>Nama</Th><Th>Telepon</Th><Th>Email</Th>
              <Th>Bank</Th><Th>Jumlah</Th><Th>Status</Th><Th>Aksi</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => {
              const b = badge(p.status);
              const needsConfirm = p.status === "awaiting_verification";
              return (
                <tr key={p.id} className={`border-t border-slate-100 hover:bg-sky-50/30 ${needsConfirm ? "bg-amber-50/40" : ""}`}>
                  <Td>{formatDate(p.created_at)}</Td>
                  <Td><b>{p.nama}</b></Td>
                  <Td>{p.telepon}</Td>
                  <Td className="text-slate-600">{p.email}</Td>
                  <Td>{p.bank}</Td>
                  <Td className="font-mono">{formatRp(p.amount)}</Td>
                  <Td>
                    <span className={`px-2 py-1 rounded-full text-[10px] uppercase tracking-[0.15em] font-semibold whitespace-nowrap ${b.cls}`}>
                      {needsConfirm && <ShieldCheck size={10} className="inline mr-1 -mt-0.5" />}
                      {b.label}
                    </span>
                  </Td>
                  <Td>
                    <div className="flex gap-1">
                      {needsConfirm && (
                        <button
                          onClick={() => onConfirm(p.id)}
                          data-testid={`confirm-payment-${p.id.slice(0,8)}`}
                          title="Konfirmasi pembayaran"
                          className="px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-semibold"
                        >
                          <Check size={13} /> Konfirmasi
                        </button>
                      )}
                      <ActionIcon href={waLink(p.telepon, `Halo ${p.nama}, terima kasih telah memesan di AI AT Studio.`)} title="WhatsApp" color="text-emerald-600">
                        <MessageCircle size={15} />
                      </ActionIcon>
                      <ActionIcon href={`mailto:${p.email}`} title="Email" color="text-sky-600">
                        <Mail size={15} />
                      </ActionIcon>
                      <button onClick={() => onDelete(p.id)} className="p-2 hover:bg-red-50 rounded-lg text-red-500" title="Hapus">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ConsultationTable({ rows, onDelete }) {
  if (!rows.length) return <Empty msg="Belum ada konsultasi." />;
  return (
    <div className="bg-white border border-sky-100 rounded-2xl overflow-hidden shadow-sm" data-testid="consultations-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-sky-50/60 text-[11px] uppercase tracking-[0.15em] text-slate-600">
            <tr>
              <Th>Tanggal</Th><Th>Nama</Th><Th>Telepon</Th><Th>Email</Th>
              <Th>Ruangan</Th><Th>Budget</Th><Th>Gaya</Th><Th>Aksi</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-sky-50/30">
                <Td>{formatDate(c.created_at)}</Td>
                <Td><b>{c.nama}</b></Td>
                <Td>{c.telepon}</Td>
                <Td className="text-slate-600">{c.email}</Td>
                <Td>{c.jenis_ruangan}</Td>
                <Td>{c.budget || "-"}</Td>
                <Td className="max-w-[200px] truncate">{c.style_preference || "-"}</Td>
                <Td>
                  <div className="flex gap-1">
                    <ActionIcon href={waLink(c.telepon, `Halo ${c.nama}, terima kasih telah mendaftar konsultasi di AI AT Studio. Tim kami akan menghubungi Anda untuk diskusi lebih lanjut.`)} title="WhatsApp" color="text-emerald-600">
                      <MessageCircle size={15} />
                    </ActionIcon>
                    <ActionIcon href={`mailto:${c.email}`} title="Email" color="text-sky-600">
                      <Mail size={15} />
                    </ActionIcon>
                    <button onClick={() => onDelete(c.id)} className="p-2 hover:bg-red-50 rounded-lg text-red-500" title="Hapus">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ActionIcon({ href, title, children, color }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" title={title} className={`p-2 hover:bg-sky-50 rounded-lg ${color}`}>
      {children}
    </a>
  );
}

function Th({ children }) {
  return <th className="text-left px-4 py-3 font-semibold whitespace-nowrap">{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 text-slate-800 whitespace-nowrap ${className}`}>{children}</td>;
}
function Empty({ msg }) {
  return (
    <div className="bg-white border border-dashed border-sky-200 rounded-2xl py-20 text-center text-slate-500">{msg}</div>
  );
}
